import { useQuery } from "@tanstack/react-query";
import { Link, linkOptions } from "@tanstack/react-router";

import { pendingIntakeFilesQueryOptions } from "../../api/intake";
import { useSession } from "../../api/session";
import { companyIdentityQueryOptions } from "../../api/settings";
import { workItemsQueryOptions } from "../../api/work";
import { ORGANIZATION } from "../../demo-data/organization";
import { COMING_SOON_SECTIONS, SECTION_IDS, SECTION_LABELS, SECTION_PATHS } from "../../domain/sections";
import { MEMBER_ROLE_LABELS } from "../../domain/settings";
import { initialsOf } from "../../lib/format";
import { BriefcaseIcon, LogoIcon, PersonIcon, SettingsIcon, TrayIcon } from "../icons";
import { Avatar } from "../ui/avatar";
import { NewMenu } from "./new-menu";
import { SidebarSearch } from "./sidebar-search";

// Rail rows: 13px, 36px tall, 6px radius, opacity-based states, no borders. The router sets aria-current on the
// active link, so the active style hangs off that attribute.
const railLinkClassName =
	"flex items-center gap-[9px] px-2.5 py-2 mt-px rounded-ctl select-none text-body transition-all duration-[120ms] text-[#FAF8F3]/65 hover:bg-white/[0.06] hover:text-[#FAF8F3]/90 aria-[current=page]:bg-white/10 aria-[current=page]:font-medium aria-[current=page]:text-[#FAF8F3] [&_svg]:w-3.5 [&_svg]:h-3.5 [&_svg]:stroke-current [&_svg]:fill-none [&_svg]:stroke-[1.7] [&_svg]:shrink-0";

// A section still to come keeps its row's place and shape, dimmed and with a "Soon" badge, but it is no link: nothing to
// hover, press or focus.
const comingSoonRowClassName =
	"flex items-center gap-[9px] px-2.5 py-2 mt-px rounded-ctl select-none text-body cursor-default text-[#FAF8F3]/35";

const railBadgeClassName =
	"ml-auto min-w-4 rounded-pill bg-white/[0.08] px-1.5 py-px text-center text-label text-[#FAF8F3]/55";

const workspaceLinks = linkOptions([
	{ to: "/work", label: "Work", icon: <BriefcaseIcon /> },
	{ to: "/directory", label: "Directory", icon: <PersonIcon /> },
	{ to: "/intake", label: "Intake", icon: <TrayIcon /> },
]);

/**
 * Fixed 220px dark rail, dark in both themes. It clips nothing of its own — the search box's dropdown hangs out over the
 * page — and the shell around it keeps anything taller than the window from showing.
 */
export function Sidebar() {
	// The company name is edited in Settings › Identity; the default shows until the query resolves.
	const { data: identity } = useQuery(companyIdentityQueryOptions());
	// The profile shows whoever is signed in; the root route's guard has already asked the API who that is.
	const { member: you } = useSession();
	// Work's badge counts the open items this member may see, and Intake's the uploads still on their way to storage;
	// each shows nothing while there are none.
	const { data: work } = useQuery(workItemsQueryOptions());
	const openWork = work?.total ?? 0;
	const { data: pending } = useQuery(pendingIntakeFilesQueryOptions());
	const uploading = pending?.length ?? 0;
	const badges: Record<string, number | undefined> = {
		"/work": openWork === 0 ? undefined : openWork,
		"/intake": uploading === 0 ? undefined : uploading,
	};

	return (
		<aside aria-label="Sidebar" className="flex h-dvh w-rail min-w-rail shrink-0 flex-col bg-rail text-rail-ink">
			<Link
				to="/"
				activeOptions={{ exact: true }}
				className="flex shrink-0 items-center gap-2.5 px-[18px] pt-5 pb-2.5"
			>
				<LogoIcon className="h-[30px] w-[30px] shrink-0" />
				<span className="block text-body leading-[1.2] font-semibold">
					{identity?.companyName ?? ORGANIZATION.name}
				</span>
			</Link>

			<SidebarSearch />

			<nav aria-label="Workspace" className="px-2.5 pb-1">
				{workspaceLinks.map(({ label, icon, ...options }) => {
					const badge = badges[options.to];
					return (
						<Link key={options.to} {...options} className={railLinkClassName}>
							{icon}
							<span className="flex-1 truncate">{label}</span>
							{badge === undefined ? null : <span className={railBadgeClassName}>{badge}</span>}
						</Link>
					);
				})}
			</nav>

			<NewMenu />

			<nav aria-label="Sections" className="thin-scroll flex-1 overflow-y-auto px-2.5 py-1.5">
				{SECTION_IDS.map(id =>
					COMING_SOON_SECTIONS.has(id) ? (
						<span key={id} className={comingSoonRowClassName}>
							<span className="flex-1 truncate">{SECTION_LABELS[id]}</span>
							<span className={railBadgeClassName}>Soon</span>
						</span>
					) : (
						<Link key={id} to={SECTION_PATHS[id]} className={railLinkClassName}>
							<span className="flex-1 truncate">{SECTION_LABELS[id]}</span>
						</Link>
					),
				)}
			</nav>

			{/* The profile opens settings. Its accessible name is the visible name and role followed by "Settings". */}
			<div className="shrink-0 border-t border-white/[0.08] px-2.5 py-1.5">
				<Link
					to="/settings"
					className="flex press items-center gap-2.5 rounded-ctl px-2 py-1.5 transition-colors duration-[120ms] hover:bg-white/[0.06] aria-[current=page]:bg-white/10"
				>
					<Avatar initials={initialsOf(you.name)} size="sm" from="var(--legal)" to="var(--people)" />
					<span className="min-w-0 flex-1">
						<span className="block truncate text-ctl font-medium">{you.name}</span>{" "}
						<span className="block truncate text-micro text-[#FAF8F3]/45">
							{MEMBER_ROLE_LABELS[you.role]}
						</span>
					</span>{" "}
					<span className="sr-only">Settings</span>
					<SettingsIcon
						className="h-3.5 w-3.5 shrink-0 fill-none stroke-[#FAF8F3]/35 stroke-[1.6]"
						strokeLinecap="round"
					/>
				</Link>
			</div>
		</aside>
	);
}
